import {CreateExtArgs, Extension, pointerUtils} from '@wix/document-manager-core'
import type {
    CompRef,
    ResponsiveBreakpointsDataOptions,
    VariantPointer,
    TrainingSample,
    TrainingDataset
} from '@wix/document-services-types'
import _ from 'lodash'
import {COMP_TYPES, MASTER_PAGE_ID, VIEW_MODES} from '../../constants/constants'
import type {PageExtensionAPI} from '../page'
import type {StructureExtensionAPI} from '../structure'
import {getMobileBreakpoint} from '../mobileAlgo/preprocess/utils/mobileBreakPoint'
import {DEFAULT_NAMESPACES} from './constants'
import {createFilters} from './filters'
import {createDependenciesCollector, createExtractors, DependenciesCollector} from './extractors'
import {createTraversal} from './traversal'
import type {SectionAPI, PageAPI, SiteAPI, ResponsiveBreakpointsDataExtensionAPI} from './types'
import type {DataValidatorFn} from './validations'

export type {ResponsiveBreakpointsDataExtensionAPI, ResponsiveBreakpointsDataAPI} from './types'

const {getPointer} = pointerUtils

const MOBILE_MAX_BREAKPOINT_WIDTH = 390
const DESKTOP_MIN_REFWIDTH = 1280

const createExtension = (): Extension => {
    const createExtensionAPI = (extArgs: CreateExtArgs): ResponsiveBreakpointsDataExtensionAPI => {
        const {dal, pointers, extensionAPI} = extArgs
        const pagesApi = () => (extensionAPI as PageExtensionAPI).page

        const filters = createFilters(extArgs)
        const extractors = createExtractors(extArgs, filters)
        const traversal = createTraversal(extArgs, filters, extractors)

        const getTargetBreakpointForSection = (
            sectionPointer: CompRef,
            pagePointer: CompRef
        ): VariantPointer | undefined => {
            const {components} = extensionAPI as StructureExtensionAPI
            const compType = components.getComponentType(sectionPointer)
            const isHeaderOrFooter = compType === COMP_TYPES.HEADER_SECTION || compType === COMP_TYPES.FOOTER_SECTION
            const breakpointOwner = isHeaderOrFooter ? sectionPointer : pagePointer
            return getMobileBreakpoint(dal, pointers, breakpointOwner)
        }

        const resolveTargetBreakpoint = (
            sectionPointer: CompRef,
            pagePointer: CompRef,
            providedBreakpoint?: VariantPointer
        ): VariantPointer | undefined => {
            return providedBreakpoint ?? getTargetBreakpointForSection(sectionPointer, pagePointer)
        }

        const getNamespacesToExtract = (additionalNamespaces?: string[]): string[] => {
            const baseNamespaces = [...DEFAULT_NAMESPACES]
            if (additionalNamespaces) {
                return _.uniq([...baseNamespaces, ...additionalNamespaces])
            }
            return baseNamespaces
        }

        const buildGlobalData = (collector: DependenciesCollector): TrainingSample['global'] => {
            const styles = extractors.extractGlobalStyles(collector)
            const variables = extractors.extractGlobalVariables(collector)
            const hasStyles = Object.keys(styles).length > 0
            const hasVariables = variables && Object.keys(variables).length > 0
            if (!hasStyles && !hasVariables) {
                return undefined
            }
            return {
                ...(hasStyles ? {styles} : {}),
                ...(hasVariables ? {variables} : {})
            }
        }

        const sectionAPI: SectionAPI = {
            getTrainingData(sectionId: string, options: ResponsiveBreakpointsDataOptions = {}): TrainingSample | null {
                const sectionPointer = getPointer(sectionId, VIEW_MODES.DESKTOP) as CompRef
                if (!dal.has(sectionPointer)) {
                    return null
                }
                if (filters.shouldExcludeSection({sectionPointer}, options)) {
                    return null
                }
                const pagePointer = pointers.structure.getPageOfComponent(sectionPointer)
                if (!pagePointer) {
                    return null
                }
                const namespaces = getNamespacesToExtract(options?.additionalNamespaces)
                const targetBreakpoint = resolveTargetBreakpoint(
                    sectionPointer,
                    pagePointer as CompRef,
                    options?.targetBreakpoint
                )
                if (!targetBreakpoint) {
                    return null
                }
                const collector = createDependenciesCollector()
                const desktopValidator: DataValidatorFn = data => {
                    if (
                        filters.isLayoutRefWidthOutOfBounds({layout: data.layout, min: DESKTOP_MIN_REFWIDTH}, options)
                    ) {
                        return 'skip_section'
                    }
                    return 'valid'
                }
                const desktopTree = traversal.buildNestedDesktopTree(
                    sectionPointer,
                    namespaces,
                    collector,
                    desktopValidator
                )
                const mobileValidator: DataValidatorFn = data => {
                    if (
                        filters.isLayoutRefWidthOutOfBounds(
                            {layout: data.layout, max: MOBILE_MAX_BREAKPOINT_WIDTH},
                            options
                        )
                    ) {
                        return 'skip_section'
                    }
                    return 'valid'
                }
                const breakpointTree = traversal.buildNestedBreakpointTree(
                    sectionPointer,
                    [targetBreakpoint],
                    namespaces,
                    collector,
                    mobileValidator
                )
                if (!desktopTree || !breakpointTree) {
                    return null
                }
                const sectionMetadata = extractors.extractSectionMetadata(collector)
                return {
                    pageId: pagePointer.id,
                    sectionId,
                    input: desktopTree,
                    output: breakpointTree,
                    global: buildGlobalData(collector),
                    ...(Object.keys(sectionMetadata).length > 0 ? {metadata: sectionMetadata} : {})
                }
            },

            applyMobileStructure(sectionRef: CompRef, output: TrainingSample['output']): void {
                const pagePointer = pointers.structure.getPageOfComponent(sectionRef)
                if (!pagePointer) return
                const targetBreakpoint = getTargetBreakpointForSection(sectionRef, pagePointer as CompRef)
                if (!targetBreakpoint) {
                    throw new Error('No mobile breakpoint found for section')
                }
                traversal.applyNestedTree(output, targetBreakpoint)
            }
        }

        const getSectionsForPage = (pageId: string, includeSharedParts: boolean): string[] => {
            const pagePointer = pointers.structure.getPage(pageId, VIEW_MODES.DESKTOP)
            if (!pagePointer) {
                return []
            }
            return traversal
                .getComponentsToTraverse(pagePointer, includeSharedParts, pageId === MASTER_PAGE_ID)
                .map(s => s.id)
        }

        const pageAPI: PageAPI = {
            getTrainingData(pageId: string, options?: ResponsiveBreakpointsDataOptions): TrainingDataset {
                const includeSharedParts = options?.includeSharedParts ?? false
                const sectionIds = getSectionsForPage(pageId, includeSharedParts)
                const samples: TrainingSample[] = []

                for (const sectionId of sectionIds) {
                    const sample = sectionAPI.getTrainingData(sectionId, options)
                    if (sample) {
                        samples.push(sample)
                    }
                }

                return {
                    samples,
                    metadata: {totalPages: 1, totalSections: samples.length, totalSamples: samples.length}
                }
            }
        }

        const siteAPI: SiteAPI = {
            getTrainingData(options?: ResponsiveBreakpointsDataOptions): TrainingDataset {
                const allPageIds = pagesApi().getAllPagesIds(true)

                const allSamples: TrainingSample[] = allPageIds.flatMap(
                    pageId => pageAPI.getTrainingData(pageId, options).samples
                )

                return {
                    samples: allSamples,
                    metadata: {
                        totalPages: allPageIds.length,
                        totalSections: allSamples.length,
                        totalSamples: allSamples.length
                    }
                }
            }
        }

        return {
            responsiveBreakpointsData: {
                section: sectionAPI,
                page: pageAPI,
                site: siteAPI
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: CreateExtArgs) => {
        const {responsiveBreakpointsData} = extensionAPI as ResponsiveBreakpointsDataExtensionAPI
        return {
            responsiveBreakpointsData: {
                getTrainingData: responsiveBreakpointsData.site.getTrainingData
            }
        }
    }

    return {
        name: 'responsiveBreakpointsData',
        dependencies: new Set([
            'variants',
            'variables',
            'dataModel',
            'page',
            'structure',
            'layout',
            'manifest',
            'innerElements',
            'builderComponents',
            'tpa',
            'platform',
            'style'
        ]),
        createExtensionAPI,
        createPublicAPI
    }
}

export {createExtension}
